import * as React from 'react'
import { useState } from 'react'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'

import DeleteModal from 'modules/timeoff/components/BlockedPeriod/PolicyDetails/TimePeriods/DeleteModal'
import FormModal from 'modules/timeoff/components/BlockedPeriod/PolicyDetails/TimePeriods/FormModal'
import PeriodItem from 'modules/timeoff/components/BlockedPeriod/PolicyDetails/TimePeriods/PeriodItem'
import type PolicyForm from 'modules/timeoff/lib/BlockedPeriods/PolicyUpdateForm'
import type { ITimePeriod as TimePeriodAttributes } from 'modules/timeoff/lib/BlockedPeriods/TimePeriod'
import TimePeriodForm from 'modules/timeoff/lib/BlockedPeriods/TimePeriodForm'

type PeriodItemProps = React.ComponentProps<typeof PeriodItem>

interface Props {
  policyForm: PolicyForm
  onDeletePeriod: () => void
}

const PeriodList = ({ policyForm, onDeletePeriod }: Props) => {
  const timePeriods = policyForm.get('time_periods').value || []
  const timePeriodForm = (periodData: TimePeriodAttributes) => new TimePeriodForm(periodData)

  const [periodToEdit, setPeriodToEdit] = useState<TimePeriodAttributes | null | undefined>(null)
  const [periodToDelete, setPeriodToDelete] = useState<TimePeriodAttributes | null | undefined>(
    null
  )

  const onDelete: PeriodItemProps['onDelete'] = (period) => setPeriodToDelete(period)

  const onEdit: PeriodItemProps['onEdit'] = (period) => {
    setPeriodToEdit(period)
  }

  const closeEditModal = () => {
    setPeriodToEdit(null)
  }

  const onSave = (periodEdited: TimePeriodAttributes) => {
    const otherPeriods = timePeriods.filter(
      (p: { id: string | number }) => p.id !== periodEdited.id
    )
    const editedPeriods = [periodEdited, ...otherPeriods]

    policyForm.setValues({
      time_periods: editedPeriods,
    })

    closeEditModal()
  }

  const closeDeleteModal = () => setPeriodToDelete(null)

  const onConfirmDelete = () => {
    if (!periodToDelete) return

    periodToDelete._destroy = true

    onDeletePeriod()
    closeDeleteModal()
  }

  const filteredPeriods = timePeriods.length
    ? timePeriods.filter((period: TimePeriodAttributes) => !period._destroy)
    : []

  return (
    <>
      <BoxList>
        {filteredPeriods.map((payload: object, i: number) => {
          return <PeriodItem key={i} payload={payload} onEdit={onEdit} onDelete={onDelete} />
        })}
      </BoxList>
      {!!periodToEdit && (
        <FormModal
          timePeriodForm={timePeriodForm(periodToEdit)}
          closeModal={closeEditModal}
          onSave={onSave}
        />
      )}
      {!!periodToDelete && (
        <DeleteModal closeModal={closeDeleteModal} onConfirm={onConfirmDelete} />
      )}
    </>
  )
}

export default observer(PeriodList)
